/**
 * Search across every item and project.
 *
 * A segment of the Projects bucket rather than a tab of its own. It had a tab
 * in the five-tab bar, which cost a permanent fifth of the bar for something
 * used occasionally; as a header field it cost a row on every screen. As a
 * segment it costs nothing until you go looking for it, and it sits next to
 * the two lists it searches.
 *
 * The query is local state, not ChromeContext — a search you ran here should
 * not silently filter the Tasks list when you switch back, which is what the
 * shared query did.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ItemRow } from "@/components/ItemRow";
import { card } from "@/components/atoms";
import { matchesQuery } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { subtaskCounts, useAllItems, useAllSubtasks, useProjects, useToggleItem } from "@/hooks/useData";
import { itemsById as buildItemsById } from "@/lib/deps";
import { progressCounts } from "@/lib/hierarchy";
import type { Project } from "@/lib/types";

import { SectionLabel } from "../components";

export function SearchSegment() {
  const [q, setQ] = useState("");
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const { data: subs = [] } = useAllSubtasks();
  const toggle = useToggleItem();
  const { openItem } = useTaskEditor();

  const projectById = new Map<string, Project>(projects.map((p) => [p.id, p]));
  const byId = buildItemsById(items);
  const subCounts = progressCounts(items, subtaskCounts(subs));
  const term = q.trim();

  const hits = useMemo(() => {
    if (!term) return { items: [], projects: [] };
    return {
      // Open items first, then by due date — the same order the desktop
      // results list uses, so the two do not disagree.
      items: [...items]
        .filter((i) => matchesQuery(i.title, term))
        .sort((a, b) => {
          const ad = a.status === "done" ? 1 : 0, bd = b.status === "done" ? 1 : 0;
          if (ad !== bd) return ad - bd;
          return (a.due || "9999-99-99").localeCompare(b.due || "9999-99-99");
        })
        .slice(0, 60),
      projects: projects.filter((p) => matchesQuery(p.name, term) || matchesQuery(p.client || "", term)),
    };
  }, [items, projects, term]);

  const nothing = term && !hits.items.length && !hits.projects.length;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ ...card, display: "flex", alignItems: "center", gap: 9, padding: "2px 12px", borderRadius: 12 }}>
        <span style={{ color: "var(--muted2)", flex: "none" }}>⌕</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tasks and projects…"
          // Not type="search": Safari draws its OWN clear button for that, so
          // the field ended up with two × side by side. enterKeyHint gets the
          // Search key on the iOS keyboard without the extra chrome.
          type="text"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          aria-label="Search tasks and projects"
          style={{
            flex: 1, minWidth: 0, minHeight: 46, border: "none", background: "none",
            color: "var(--ink)", outline: "none",
          }}
        />
        {q && (
          <button
            onClick={() => setQ("")}
            aria-label="Clear search"
            style={{ flex: "none", border: "none", background: "none", color: "var(--muted2)", fontSize: 18, cursor: "pointer", padding: "0 4px", minHeight: 44 }}
          >×</button>
        )}
      </div>

      {!term && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
          Search every project at once.
        </div>
      )}

      {nothing && (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
          Nothing matches “{term}”.
        </div>
      )}

      {hits.projects.length > 0 && (
        <>
          <SectionLabel right={`${hits.projects.length}`}>Projects</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {hits.projects.map((p) => (
              <Link
                key={p.id}
                to={`/project/${p.id}`}
                style={{ ...card, borderRadius: 12, padding: "12px 14px", textDecoration: "none", display: "flex", alignItems: "center", gap: 9, minHeight: 52 }}
              >
                <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flex: "none" }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </span>
                  {p.client && (
                    <span style={{ display: "block", fontSize: 12, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.client}
                    </span>
                  )}
                </span>
                <span aria-hidden style={{ flex: "none", color: "var(--muted2)", fontSize: 15 }}>›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      {hits.items.length > 0 && (
        <>
          <SectionLabel right={`${hits.items.length}`}>Tasks</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {hits.items.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                projectById={projectById}
                onToggle={toggle}
                onOpen={openItem}
                itemsById={byId}
                subCounts={subCounts}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
